/**
 * DeveloperRegistrationForm - Developer Program Registration Form
 *
 * React Hook Form + Zod validation with Tailwind CSS styling
 * Uses i18n for labels, integrates with backend for access code validation
 */

import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { FormError } from '@/components/ui';
import {
  useDeveloperRegistrationForm,
  type DeveloperRegistrationFormData,
} from './DeveloperRegistrationForm.hook';
import { registrationSchema, type RegistrationFormValues } from './developer-registration.schema';

// ============================================================================
// Props Interface
// ============================================================================

export interface DeveloperRegistrationFormProps {
  antiforgeryToken: string;
}

// ============================================================================
// Main Component
// ============================================================================

export const DeveloperRegistrationForm = React.forwardRef<
  HTMLDivElement,
  DeveloperRegistrationFormProps
>(({ antiforgeryToken }, ref) => {
  const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);

  // Custom hook for form logic
  const {
    accessCodeValidating,
    accessCodeResult,
    accessCodeError,
    isSubmitting,
    submitError,
    fieldErrors,
    validateAccessCode,
    submitForm,
    setSubmitError,
  } = useDeveloperRegistrationForm({ antiforgeryToken });

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    mode: 'onBlur', // Validate on blur for better UX
    defaultValues: {
      companyName: '',
      companyAddress: '',
      ownerContactName: '',
      ownerContactEmail: '',
      technicalContactName: '',
      technicalContactEmail: '',
      accessCode: '',
    },
  });

  // Watch access code for validation
  const accessCodeValue = watch('accessCode');

  // Handle access code blur for validation
  const handleAccessCodeBlur = useCallback(() => {
    if (accessCodeValue) {
      validateAccessCode(accessCodeValue);
    }
  }, [accessCodeValue, validateAccessCode]);

  // Handle form submission
  const onSubmit = useCallback(
    (data: RegistrationFormValues) => {
      const formData: DeveloperRegistrationFormData = {
        companyName: data.companyName,
        companyAddress: data.companyAddress,
        ownerContactName: data.ownerContactName,
        ownerContactEmail: data.ownerContactEmail,
        technicalContactName: data.technicalContactName || undefined,
        technicalContactEmail: data.technicalContactEmail || undefined,
        accessCode: data.accessCode || undefined,
      };
      submitForm(formData);
    },
    [submitForm]
  );

  // Helper to get combined error for a field
  const getFieldError = (fieldName: keyof RegistrationFormValues): string | undefined => {
    return errors[fieldName]?.message || fieldErrors[fieldName];
  };

  // Helper to determine if field has error
  const hasFieldError = (fieldName: keyof RegistrationFormValues): boolean => {
    return !!errors[fieldName] || !!fieldErrors[fieldName];
  };

  const inputBaseClass =
    'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const inputErrorClass = 'border-red-500';
  const inputSuccessClass = 'border-green-500';
  const inputNormalClass = 'border-gray-300';

  return (
    <div ref={ref} className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <img
          src="/img/ecgridlogo2x.png"
          alt="ECGrid Logo"
          className="w-32 h-32 mx-auto mb-4"
        />
        <h1 className="text-2xl font-bold text-gray-900">
          {t('DeveloperRegistration_Title', 'ECGrid Developer Program')}
        </h1>
        <p className="text-gray-600 mt-2">
          {t('DeveloperRegistration_Subtitle', 'Get started with ECGrid EDI integration')}
        </p>
      </div>

      <hr className="my-6 border-gray-200" />

      <p className="text-center text-gray-600 mb-6">
        {t(
          'DeveloperRegistration_Description',
          "Complete the form below to register for the ECGrid Developer Program. After submitting, you'll be redirected to complete your payment through Stripe."
        )}
      </p>

      {/* Submit Error Banner */}
      {submitError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative">
          <span>{submitError}</span>
          <button
            className="absolute top-0 right-0 px-4 py-3"
            onClick={() => setSubmitError(null)}
          >
            &times;
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Company Information Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {t('DeveloperRegistration_CompanyInfo', 'Company Information')}
          </h2>

          {/* Company Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('DeveloperRegistration_CompanyName', 'Company Name')} *
            </label>
            <input
              {...register('companyName')}
              type="text"
              placeholder={t(
                'DeveloperRegistration_CompanyName_Placeholder',
                'Enter your company name'
              )}
              disabled={isSubmitting}
              className={`${inputBaseClass} ${hasFieldError('companyName') ? inputErrorClass : inputNormalClass}`}
            />
            <FormError message={getFieldError('companyName')} />
          </div>

          {/* Company Address */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('DeveloperRegistration_CompanyAddress', 'Company Address')} *
            </label>
            <textarea
              {...register('companyAddress')}
              placeholder={t(
                'DeveloperRegistration_CompanyAddress_Placeholder',
                'Enter your company address'
              )}
              rows={3}
              disabled={isSubmitting}
              className={`${inputBaseClass} ${hasFieldError('companyAddress') ? inputErrorClass : inputNormalClass}`}
            />
            <FormError message={getFieldError('companyAddress')} />
          </div>
        </div>

        {/* Owner Contact Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {t('DeveloperRegistration_OwnerContact', 'Owner Contact')}
          </h2>

          {/* Owner Contact Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('DeveloperRegistration_OwnerContactName', 'Owner Contact Name')} *
            </label>
            <input
              {...register('ownerContactName')}
              type="text"
              placeholder={t(
                'DeveloperRegistration_OwnerContactName_Placeholder',
                'Enter owner contact name'
              )}
              disabled={isSubmitting}
              className={`${inputBaseClass} ${hasFieldError('ownerContactName') ? inputErrorClass : inputNormalClass}`}
            />
            <FormError message={getFieldError('ownerContactName')} />
          </div>

          {/* Owner Contact Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('DeveloperRegistration_OwnerContactEmail', 'Owner Contact Email')} *
            </label>
            <input
              {...register('ownerContactEmail')}
              type="email"
              placeholder={t(
                'DeveloperRegistration_OwnerContactEmail_Placeholder',
                'Enter owner contact email'
              )}
              disabled={isSubmitting}
              className={`${inputBaseClass} ${hasFieldError('ownerContactEmail') ? inputErrorClass : inputNormalClass}`}
            />
            <FormError message={getFieldError('ownerContactEmail')} />
          </div>
        </div>

        {/* Technical Contact Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {t('DeveloperRegistration_TechnicalContact', 'Technical Contact')}
          </h2>

          {/* Technical Contact Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('DeveloperRegistration_TechnicalContactName', 'Technical Contact Name (Optional)')}
            </label>
            <input
              {...register('technicalContactName')}
              type="text"
              placeholder={t(
                'DeveloperRegistration_TechnicalContactName_Placeholder',
                'Enter technical contact name'
              )}
              disabled={isSubmitting}
              className={`${inputBaseClass} ${hasFieldError('technicalContactName') ? inputErrorClass : inputNormalClass}`}
            />
            <FormError message={getFieldError('technicalContactName')} />
          </div>

          {/* Technical Contact Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t(
                'DeveloperRegistration_TechnicalContactEmail',
                'Technical Contact Email (Optional)'
              )}
            </label>
            <input
              {...register('technicalContactEmail')}
              type="email"
              placeholder={t(
                'DeveloperRegistration_TechnicalContactEmail_Placeholder',
                'Enter technical contact email'
              )}
              disabled={isSubmitting}
              className={`${inputBaseClass} ${hasFieldError('technicalContactEmail') ? inputErrorClass : inputNormalClass}`}
            />
            <FormError message={getFieldError('technicalContactEmail')} />
          </div>
        </div>

        {/* Access Code Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {t('DeveloperRegistration_AccessCode', 'Access Code')}
          </h2>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('DeveloperRegistration_AccessCodeLabel', 'ECGrid Access Code (Optional)')}
            </label>
            <div className="relative">
              <input
                {...register('accessCode')}
                type="text"
                placeholder={t(
                  'DeveloperRegistration_AccessCode_Placeholder',
                  'Enter access code if you have one'
                )}
                disabled={isSubmitting}
                onBlur={handleAccessCodeBlur}
                className={`${inputBaseClass} ${
                  accessCodeError
                    ? inputErrorClass
                    : accessCodeResult?.isValid
                      ? inputSuccessClass
                      : inputNormalClass
                }`}
              />
              {accessCodeValidating && (
                <span className="absolute right-3 top-2.5 text-gray-400">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                </span>
              )}
              {!accessCodeValidating && accessCodeResult?.isValid && (
                <span className="absolute right-3 top-2.5 text-green-500">✓</span>
              )}
              {!accessCodeValidating && accessCodeError && (
                <span className="absolute right-3 top-2.5 text-red-500">!</span>
              )}
            </div>
            <p className="text-gray-500 text-xs mt-1">
              {t(
                'DeveloperRegistration_AccessCodeHelp',
                'If you received an access code from a sales representative, enter it here.'
              )}
            </p>
            <FormError message={accessCodeError} />
            {accessCodeResult?.isValid && accessCodeResult?.salespersonEmail && (
              <p className="text-green-600 text-xs mt-1">
                {t('DeveloperRegistration_ValidCode_Referred', 'Valid code - referred by')}{' '}
                {accessCodeResult.salespersonEmail}
              </p>
            )}
            {accessCodeResult?.isValid &&
              !accessCodeResult?.salespersonEmail &&
              accessCodeValue && (
                <p className="text-green-600 text-xs mt-1">
                  {t('DeveloperRegistration_ValidCode', 'Valid access code')}
                </p>
              )}
          </div>
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-4 text-white font-medium rounded-md transition-colors ${
              isSubmitting
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSubmitting
              ? t('DeveloperRegistration_Submitting', 'Processing...')
              : t('DeveloperRegistration_Submit', 'Continue to Payment')}
          </button>
        </div>

        {/* Required Fields Note */}
        <p className="text-center text-gray-500 text-xs">
          {t('DeveloperRegistration_RequiredFields', '* Required fields')}
        </p>
      </form>
    </div>
  );
});

DeveloperRegistrationForm.displayName = 'DeveloperRegistrationForm';
